<ng-container *transloco="let t; read: 'providers.feedFiles'">
  <!-- One hidden picker shared by every upload action -->
  <input #picker type="file" class="sr-only" [attr.accept]="pickerAccept() || accept() || null" (change)="onPicked($event)" />

  <div class="flex flex-col gap-3">
    @if (overLimit() && overLimitWarning()) {
      <p class="m-0 rounded-control bg-danger-surface px-3 py-2 text-[13px] leading-5 text-danger">{{ overLimitWarning() }}</p>
    }
    @if (missingContent() > 0) {
      <p class="m-0 rounded-control bg-sunken px-3 py-2 text-[12px] leading-[18px] text-ink-2">{{ t('list.holdBack') }}</p>
    }
    @if (requiredRoles().length) {
      <!-- Required roles: core delivers no file set until each has a file with content -->
      <div class="flex flex-wrap items-center gap-x-4 gap-y-1.5 rounded-control bg-sunken px-3 py-2 text-[12px] leading-[18px]">
        <span class="font-semibold text-ink-2">{{ t('list.requiredRoles') }}</span>
        @for (role of requiredRoles(); track role.name) {
          <span class="flex items-center gap-1.5" [class.text-ink-1]="role.filled" [class.text-danger]="!role.filled">
            <span class="inline-block h-2 w-2 rounded-full" [class.bg-success]="role.filled" [class.bg-danger]="!role.filled"></span>
            {{ role.label }} · {{ role.filled ? t('list.roleFilled') : t('list.roleMissing') }}
          </span>
        }
      </div>
    }

    @if (isLoading()) {
      <span class="py-3 text-[13px] text-ink-3">—</span>
    } @else {
      @for (file of files(); track file.id) {
        <div class="flex flex-col gap-2 rounded-control p-4 shadow-hairline">
          <!-- Name, role, source -->
          <div class="flex flex-wrap items-center gap-x-3 gap-y-1">
            <span class="truncate text-[14px] font-semibold text-ink-1">{{ file.filename }}</span>
            @if (file.role) {
              <span class="text-[12px]" [class.text-ink-3]="!roleUndeclared(file)" [class.text-danger]="roleUndeclared(file)">{{ roleText(file.role) }}</span>
            }
            @if (roleUndeclared(file)) {
              <span class="text-[12px] font-semibold text-danger">{{ file.role ? t('list.undeclaredRole') : t('list.noRole') }}</span>
            }
            <app-label-badge>{{ t('source.' + file.sourceType) }}</app-label-badge>
            @if (file.sourceType === 'URL' && file.sourceConfig?.URL?.url; as url) {
              <span class="min-w-0 max-w-[360px] truncate text-[12px] text-ink-3" [attr.title]="url">{{ url }}</span>
            }
          </div>

          <!-- Content -->
          <div class="text-[13px]">
            @if (file.content; as c) {
              <span class="text-ink-2" [attr.title]="c.updatedAt">
                {{ t('list.content', { size: formatBytes(c.size), time: relativeTime(c.updatedAt) }) }}
              </span>
            } @else {
              <span class="font-semibold text-warning">{{ t('list.noContent') }}</span>
            }
          </div>

          <!-- Watching (URL only) -->
          @if (file.sourceType === 'URL') {
            @if (isChecking(file)) {
              <span class="text-[12px] text-ink-3">{{ t('list.checking') }}</span>
            } @else if (file.check; as check) {
              @if (check.lastResult === 'FAILED') {
                <div class="flex flex-col gap-0.5 rounded-control bg-danger-surface px-3 py-2 text-[12px] leading-[18px] text-danger">
                  <span>
                    @if (check.lastErrorCode) {
                      <span class="font-bold">{{ t('fetchError.' + check.lastErrorCode) }}</span>
                    }
                    {{ check.lastError }}
                  </span>
                  <span>
                    {{ check.lastSuccessAt ? t('list.failingSince', { time: relativeTime(check.lastSuccessAt), count: check.consecutiveFailures })
                      : t('list.neverSucceeded', { count: check.consecutiveFailures }) }}
                  </span>
                </div>
              } @else if (check.lastCheckAt) {
                <span class="flex items-center gap-1.5 text-[12px] text-ink-2">
                  <span class="h-2 w-2 flex-none rounded-full bg-[var(--colorscales-green-500)]"></span>
                  {{ t('list.checkedOk', { time: relativeTime(check.lastCheckAt) }) }}
                  @if (check.nextCheckAt) {
                    · {{ t('list.nextCheck', { time: (check.nextCheckAt | date: 'shortTime') }) }}
                  }
                </span>
              } @else {
                <span class="text-[12px] text-ink-3">{{ t('list.awaitingFirstCheck') }}</span>
              }
            }
          }

          <!-- Actions -->
          <div class="flex flex-wrap items-center gap-x-4 gap-y-1 pt-1 text-[13px] font-semibold">
            <button type="button" (click)="upload(file)" [disabled]="uploadingId() === file.id" class="text-link hover:underline disabled:opacity-40">
              {{ uploadingId() === file.id ? t('list.uploading') : t('list.upload') }}
            </button>
            @if (file.sourceType === 'URL') {
              <button type="button" (click)="checkNow(file)" [disabled]="isChecking(file)" class="text-link hover:underline disabled:opacity-40">
                {{ t('list.checkNow') }}
              </button>
            }
            @if (file.content) {
              <button type="button" (click)="download(file)" [disabled]="downloadingId() === file.id" class="text-link hover:underline disabled:opacity-40">
                {{ t('download') }}
              </button>
            }
            <button type="button" (click)="openEdit(file)" class="text-link hover:underline">{{ t('list.edit') }}</button>
            <div class="flex-1"></div>
            <button type="button" (click)="remove(file)" class="text-danger hover:underline">{{ t('list.delete') }}</button>
          </div>
        </div>
      } @empty {
        @if (quickStart()) {
          <div class="flex flex-wrap items-center gap-3">
            <button
              type="button"
              (click)="quickUpload()"
              [disabled]="uploadingId() === 'new'"
              class="h-8 rounded-control bg-action px-3 text-[13px] font-semibold text-action-text transition-[filter] duration-150 hover:brightness-90 disabled:opacity-40"
            >
              {{ uploadingId() === 'new' ? t('list.uploading') : t('list.quickUpload', { filename: defaultFilename() }) }}
            </button>
            <button
              type="button"
              (click)="openCreate('URL')"
              class="h-8 rounded-control px-3 text-[13px] font-semibold text-ink-1 shadow-hairline transition-colors duration-150 hover:bg-sunken"
            >
              {{ t('list.quickUrl') }}
            </button>
          </div>
        } @else {
          <span class="py-2 text-[13px] text-ink-3">{{ t('list.empty') }}</span>
        }
      }

      @if (canAdd() && (files().length > 0 || !quickStart())) {
        <button type="button" (click)="openCreate()" class="w-fit text-[13px] font-semibold text-link hover:underline">
          {{ t('list.add') }}
        </button>
      }
    }
  </div>

  @if (dialog(); as d) {
    <app-feed-file-dialog
      [feedId]="feedId()"
      [file]="d.file"
      [initialSource]="d.source"
      [defaultFilename]="defaultFilename()"
      [accept]="accept()"
      [filesCapability]="filesCapability()"
      [providerType]="providerType()"
      (saved)="onSaved($event)"
      (closed)="dialog.set(null)"
    />
  }
</ng-container>
